import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Map, MessageSquare, TrendingUp, Target, Settings, Menu, ChevronLeft, ChevronDown, Store, Building2, BarChart3, Users, AlertTriangle, Radar, Heart, ShoppingCart, Activity, LayoutDashboard } from 'lucide-react';
interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}
interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
}
interface MenuSection {
  id: string;
  label: string;
  icon: React.ElementType;
  items: MenuItem[];
}
const menuSections: MenuSection[] = [{
  id: 'konzern',
  label: 'Konzern-Reputation',
  icon: Building2,
  items: [{
    id: 'konzern-uebersicht',
    label: 'Übersicht',
    icon: LayoutDashboard
  }, {
    id: 'heatmap',
    label: 'Reputationskarte',
    icon: Map
  }, {
    id: 'general-stimmen',
    label: 'Stimmen & Bewertungen',
    icon: MessageSquare
  }, {
    id: 'konzern-stimmung',
    label: 'Stimmungsbarometer',
    icon: Activity
  }]
}, {
  id: 'markt',
  label: 'Filial-Reputation',
  icon: ShoppingCart,
  items: [{
    id: 'markt-stimmung',
    label: 'Stimmungsbarometer',
    icon: Heart
  }, {
    id: 'bewertungs-hub',
    label: 'Stimmen & Bewertungen',
    icon: MessageSquare
  }]
}, {
  id: 'wettbewerb',
  label: 'Mitbewerber',
  icon: Target,
  items: [{
    id: 'bewertungsvergleich',
    label: 'Bewertungsvergleich',
    icon: BarChart3
  }, {
    id: 'themenvergleich',
    label: 'Themenvergleich',
    icon: MessageSquare
  }]
}];
export function Sidebar({
  activeTab,
  onTabChange
}: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const toggleSection = (sectionId: string) => {
    if (isCollapsed) {
      setIsCollapsed(false);
      setExpandedSections([sectionId]);
    } else {
      setExpandedSections(prev => prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]);
    }
  };
  const handleItemClick = (itemId: string, sectionId: string) => {
    onTabChange(itemId);
    if (!expandedSections.includes(sectionId)) {
      setExpandedSections(prev => [...prev, sectionId]);
    }
  };
  const getActiveSectionId = () => {
    for (const section of menuSections) {
      if (section.items.some(item => item.id === activeTab)) {
        return section.id;
      }
    }
    return null;
  };
  const activeSectionId = getActiveSectionId();
  return <motion.aside initial={false} animate={{
    width: isCollapsed ? 72 : 280
  }} className="bg-lidl-blue h-screen flex flex-col fixed left-0 top-0 z-50 shadow-xl text-white overflow-hidden" transition={{
    duration: 0.2,
    ease: 'easeInOut'
  }} data-id="element-23">
      {/* Header / Logo */}
      <div className="h-[72px] flex items-center px-5 border-b border-white/10 relative shrink-0" data-id="element-24">
        <div className="flex items-center gap-2.5 overflow-hidden whitespace-nowrap" data-id="element-25">
          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/91/Lidl-Logo.svg/250px-Lidl-Logo.svg.png" alt="LIDL Logo" className="h-9 w-auto shrink-0" data-id="element-26" />
          <motion.div animate={{
          opacity: isCollapsed ? 0 : 1
        }} className="flex items-center gap-2" data-id="element-27">
            <span className="font-bold text-[22px] tracking-tight text-white" data-id="element-28">
              LIDL
            </span>
            <div className="flex flex-col leading-none" data-id="element-29">
              <span className="text-[11px] font-medium opacity-90" data-id="element-30">
                Reputation
              </span>
              <span className="text-[11px] font-light opacity-70" data-id="element-31">
                Intelligence
              </span>
            </div>
          </motion.div>
        </div>

        <button onClick={() => setIsCollapsed(!isCollapsed)} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 hover:bg-white/10 rounded-md text-white/70 hover:text-white transition-colors" data-id="element-32">
          {isCollapsed ? <Menu size={18} data-id="element-33" /> : <ChevronLeft size={18} data-id="element-34" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20" data-id="element-35">
        {menuSections.map(section => {
        const isSectionExpanded = expandedSections.includes(section.id);
        const isSectionActive = activeSectionId === section.id;
        const SectionIcon = section.icon;
        return <div key={section.id} className="mb-1" data-id="element-36">
              <button onClick={() => toggleSection(section.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${isSectionActive ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`} data-id="element-37">
                <SectionIcon size={20} className={`shrink-0 ${isSectionActive ? 'text-lidl-yellow' : ''}`} data-id="element-38" />

                <motion.span animate={{
              opacity: isCollapsed ? 0 : 1,
              width: isCollapsed ? 0 : 'auto'
            }} className="flex-1 text-left text-sm font-medium whitespace-nowrap overflow-hidden" data-id="element-39">
                  {section.label}
                </motion.span>

                {!isCollapsed && <motion.div animate={{
              rotate: isSectionExpanded ? 180 : 0
            }} transition={{
              duration: 0.2
            }} data-id="element-40">
                    <ChevronDown size={16} className="text-white/40" data-id="element-41" />
                  </motion.div>}

                {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50" data-id="element-42">
                    {section.label}
                  </div>}
              </button>

              <AnimatePresence initial={false} data-id="element-43">
                {isSectionExpanded && !isCollapsed && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-44">
                    <div className="pl-4 mt-1 space-y-0.5" data-id="element-45">
                      {section.items.map(item => {
                  const isActive = activeTab === item.id;
                  const ItemIcon = item.icon;
                  return <button key={item.id} onClick={() => handleItemClick(item.id, section.id)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-200 relative ${isActive ? 'bg-white/15 text-white font-medium' : 'text-white/50 hover:text-white hover:bg-white/5'}`} data-id="element-46">
                            {isActive && <div className="absolute left-0 inset-y-1 w-1 bg-lidl-yellow rounded-r-full" data-id="element-47" />}

                            <ItemIcon size={16} className={`shrink-0 ${isActive ? 'text-lidl-yellow' : ''}`} data-id="element-48" />

                            <span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis" data-id="element-49">
                              {item.label}
                            </span>
                          </button>;
                })}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}
      </nav>

      {/* Footer / Settings */}
      <div className="p-3 border-t border-white/10 shrink-0" data-id="element-50">
        <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-white/60 hover:text-white hover:bg-white/5" data-id="element-51">
          <Settings size={20} className="shrink-0" data-id="element-52" />
          <motion.span animate={{
          opacity: isCollapsed ? 0 : 1,
          width: isCollapsed ? 0 : 'auto'
        }} className="whitespace-nowrap overflow-hidden text-sm" data-id="element-53">
            Einstellungen
          </motion.span>
        </button>

        <motion.div animate={{
        opacity: isCollapsed ? 0 : 1
      }} className="mt-3 px-3 pb-1" data-id="element-54">
          <div className="flex items-center gap-3" data-id="element-55">
            <div className="w-9 h-9 rounded-full bg-lidl-yellow text-lidl-blue flex items-center justify-center font-bold text-sm shrink-0" data-id="element-56">
              MS
            </div>
            <div className="overflow-hidden" data-id="element-57">
              <div className="text-sm font-medium truncate" data-id="element-58">Max Schmidt</div>
              <div className="text-xs text-white/50 truncate" data-id="element-59">
                Leiter Analytics
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.aside>;
}